import {useDrawingStore} from '@/store/drawing'

interface getMapSnapshotProps {
  selectedItem: any | null | undefined
  returnBlob?: boolean // Optional prop to determine return type
}

export const getMapSnapshot = async ({
  selectedItem,
  returnBlob = false
}: getMapSnapshotProps): Promise<HTMLImageElement | Blob | null> => {
  const drawingStore = useDrawingStore()

  if (!drawingStore.stage || !drawingStore.stage.getStage()) {
    console.log('Stage not found')
    return null
  }

  if (!selectedItem || !selectedItem?.id) {
    console.log('No selected items for Snapshot')
    return null
  }

  try {
    // Hide all sign items except the specific one when printing
    drawingStore.updateSpecificItemIdToShow(selectedItem?.id)

    await nextTick()

    // Retrieve the Konva stage and make deep copies of the current scale and position
    const stage = drawingStore.stage.getStage()
    const currentScale = JSON.parse(JSON.stringify(drawingStore.scale.x))
    const currentPositionX = JSON.parse(JSON.stringify(stage.x()))
    const currentPositionY = JSON.parse(JSON.stringify(stage.y()))

    // Calculate the max of dimensions and scales
    const maxDimension = Math.max(selectedItem?.width || 25, selectedItem?.height || 25)
    const maxScale = Math.max(selectedItem?.scaleX || 1, selectedItem?.scaleY || 1)

    // Calculate the sign's dimensions using the max values (to get a square picture)
    const signWidth = maxDimension * maxScale
    const signHeight = maxDimension * maxScale

    // Calculate snapshot dimensions based on sign size
    // Make the snapshot area 12 times larger than the sign (2 * 3) to provide good context
    const snapshotWidth = signWidth * 12 * currentScale
    const snapshotHeight = signHeight * 12 * currentScale

    // Calculate the center point of the sign in stage coordinates
    const signCenterX = selectedItem.x + (signWidth / 2)
    const signCenterY = selectedItem.y + (signHeight / 2)

    // Calculate the snapshot area position to center it on the sign
    // Account for both stage position and scale
    const snapshotX = (signCenterX * currentScale + currentPositionX) - (snapshotWidth / 2)
    const snapshotY = (signCenterY * currentScale + currentPositionY) - (snapshotHeight / 2)

    // Get the data URL of the stage with calculated dimensions
    const dataUrl = stage.toDataURL({
      x: snapshotX,
      y: snapshotY,
      width: snapshotWidth,
      height: snapshotHeight,
      quality: 1,
      pixelRatio: 2,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    })

    // Wait for the next tick to restore the stage to its original state
    await nextTick(() => {
      drawingStore.updateSpecificItemIdToShow(null)
      stage.scale({ x: currentScale, y: currentScale })
      stage.position({ x: currentPositionX, y: currentPositionY })
    })

    if (returnBlob) {
      // Convert the data URL to a Blob
      return await fetch(dataUrl).then((res) => res.blob())
    } else {
      // Return an HTMLImageElement
      const mapSnapshot: HTMLImageElement = new Image()
      mapSnapshot.crossOrigin = 'Anonymous'
      mapSnapshot.src = dataUrl

      // Set dimensions while maintaining aspect ratio
      mapSnapshot.width = snapshotWidth
      mapSnapshot.height = snapshotHeight

      // Apply CSS to control the display size
      mapSnapshot.style.maxWidth = '300px' // Limit maximum width for display
      mapSnapshot.style.maxHeight = '200px' // Limit maximum height for display
      mapSnapshot.style.width = 'auto'
      mapSnapshot.style.height = 'auto'
      mapSnapshot.style.objectFit = 'contain'

      return mapSnapshot
    }
  } catch (e) {
    console.log(e)
    return null
  }
}
